import React from 'react';
import { ArticleCard } from '@/sanity/fetchArticles';
import { CategoryLink } from '@/sanity/fetchCategories';
import { Header as HeaderType } from '@/sanity/fetchLayout';
import NavigationSubMenu from './NavigationSubMenu';
import HIHButton from '@/components/base/HIHButton';
import {
  ARIA_LABEL_NAVIGATION,
  ARIA_LABEL_NAVIGATION_BOOKMARKS,
  ARIA_LABEL_NAVIGATION_PROFILE,
  URL_PAGE_APP,
  URL_PAGE_BOOKMARKS,
} from '@/shared/constants';
import styles from './NavigationMainMenu.module.scss';
import Image from 'next/image';
import { usePathname } from 'next/navigation';

interface NavigationMainMenuProps {
  appCta: HeaderType['appCta'];
  menu: HeaderType['menu'];
  articles?: ArticleCard[];
  categories?: CategoryLink[];
  isMenuOpen?: boolean;
  id: string;
}

const NavigationMainMenu: React.FC<NavigationMainMenuProps> = ({
  appCta,
  menu,
  articles,
  categories,
  isMenuOpen,
  id,
}) => {
  const pathname = usePathname();
  return (
    <ul
      id={id}
      className={[styles.menu, isMenuOpen ? styles.open : ''].filter(Boolean).join(' ')}
    >
      <div className={styles.mobileLabel}>{ARIA_LABEL_NAVIGATION}</div>
      {menu.map((item) =>
        item.hasCategoriesDropdown ? (
          <NavigationSubMenu
            key={item.link}
            title={item.title}
            articles={articles}
            categories={categories}
          />
        ) : (
          <li key={item.link} className={styles.menuItem}>
            {/* a instead of nextjs/link because the latter has a known bug regarding focus on route change which is an a11y problem here in the navigation */}
            <a className={styles.navLink} href={item.link}>
              {item.title}
            </a>
          </li>
        ),
      )}
      <div className={styles.actionsWrapper}>
        {/* Mobile Section Start */}
        <div className={styles.mobileSection}>
          <div className={styles.mobileSectionLabel}>{ARIA_LABEL_NAVIGATION_PROFILE}</div>
          <li className={styles.mobileBookmarkItem}>
            {/* a instead of nextjs/link because the latter has a known bug regarding focus on route change which is an a11y problem here in the navigation */}
            <a className={styles.mobileBookmarkLink} href={URL_PAGE_BOOKMARKS}>
              {ARIA_LABEL_NAVIGATION_BOOKMARKS}
            </a>
          </li>
        </div>
        {/* Mobile Section End */}
        {/* Desktop Section Start */}
        <a
          className={styles.desktopBookmarkLink}
          href={URL_PAGE_BOOKMARKS}
          aria-label={ARIA_LABEL_NAVIGATION_BOOKMARKS}
        >
          <Image
            src={
              pathname === URL_PAGE_BOOKMARKS
                ? '/icons/icon-bookmark-list-filled.svg'
                : '/icons/icon-bookmark-list.svg'
            }
            width={24}
            height={24}
            alt=""
            className={styles.bookmarkIcon}
          />
        </a>
        {/* Desktop Section End */}
        {appCta && (
          <li className={styles.appCtaItem}>
            <HIHButton
              link={URL_PAGE_APP}
              title={appCta.title}
              variant="secondary"
              size="sm"
              className={styles.appCtaButton}
            />
          </li>
        )}
      </div>
    </ul>
  );
};

export default NavigationMainMenu;
